// src/routes/ResumePage.js
// ─────────────────────────────────────────────────────────────────────────────
// FIX: Hidden sections now respected on the public page.
//
// Root cause: the previous `show()` helper used `!visibleSections || includes()`
// which short-circuits to TRUE whenever visibleSections is any truthy value
// (all arrays are truthy, even empty ones). So show() ALWAYS returned true,
// meaning every section always rendered regardless of what was saved.
//
// The fix: resolve visibleSections once via useMemo into a guaranteed array,
// then use a plain .includes() with NO truthy-check bypass.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";
import { FaDownload, FaSpinner } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";
import { useData } from "../hooks/useData";
import { getResumeData } from "../firebase/dataService";
import "./ResumePage.css";

const ALL_SECTION_IDS = [
  "summary", "experience", "education", "projects",
  "technicalSkills", "certifications", "languages", "volunteerAndLeadership",
];

const sortChronological = (items = []) =>
  [...items].sort((a, b) => {
    if (a.current && !b.current) return -1;
    if (!a.current && b.current) return 1;
    const toNum = (str = "") => {
      const months = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
      const parts = str.trim().toLowerCase().split(/[\s,]+/);
      const year  = parts.find((p) => /^\d{4}$/.test(p)) || "0";
      const mon   = months[parts.find((p) => months[p])] || 0;
      return parseInt(year) * 100 + mon;
    };
    return toNum(b.startDate) - toNum(a.startDate);
  });

const buildPrintHTML = (data, profile, visibleSections) => {
  // ── CRITICAL: use a plain includes() with no truthy bypass ──────────────
  const show = (id) => visibleSections.includes(id);
  const { summary, experience, education, projects, technicalSkills,
          certifications, languages, volunteerAndLeadership } = data;

  const sec = (title, content) => `<div class="cv-section"><p class="cv-section-title">${title}</p>${content}</div>`;

  const expRows = sortChronological(experience || []).map((e) => `
    <div class="exp-block">
      <div class="exp-header">
        <div><p class="exp-role">${e.role}</p><p class="exp-company">${e.company}${e.location ? ` · ${e.location}` : ""}</p></div>
        <p class="exp-period">${e.startDate} – ${e.current ? "Present" : e.endDate}</p>
      </div>
      <ul class="bullets">${(e.bullets || []).map((b) => `<li>${b}</li>`).join("")}</ul>
    </div>`).join("");

  const eduRows = sortChronological(education || []).map((e) => `
    <div class="exp-block">
      <div class="exp-header">
        <div><p class="exp-role">${e.degree}</p>
          <p class="exp-company">${e.institution}${e.location ? `, ${e.location}` : ""}</p>
          ${e.grade ? `<p class="edu-grade">${e.grade}${e.notes ? ` — ${e.notes}` : ""}</p>` : ""}
        </div>
        <p class="exp-period">${e.startDate} – ${e.current ? "Present" : e.endDate}</p>
      </div>
    </div>`).join("");

  const projRows = (projects || []).map((p) => `
    <div class="exp-block">
      <div class="exp-header">
        <div><p class="exp-role">${p.title}</p><p class="exp-company">${p.stack || ""}</p></div>
        <p class="exp-period">${p.year || ""}</p>
      </div>
      <ul class="bullets">${(p.bullets || []).map((b) => `<li>${b}</li>`).join("")}</ul>
    </div>`).join("");

  const skillRows = Object.entries(technicalSkills || {})
    .map(([cat, items]) => `<tr><td class="sk-cat">${cat}</td><td>${items.join(", ")}</td></tr>`).join("");
  const certList  = (certifications || []).map((c) => `<li>${c}</li>`).join("");
  const langList  = (languages || []).map((l) =>
    `<tr><td class="sk-cat">${l.language}</td><td>${l.level}</td></tr>`).join("");
  const volRows   = (volunteerAndLeadership || []).map((v) => `
    <div class="exp-block">
      <div class="exp-header">
        <div><p class="exp-role">${v.role}</p><p class="exp-company">${v.organisation}</p></div>
        <p class="exp-period">${v.period}</p>
      </div>
      ${v.description ? `<p class="vol-desc">${v.description}</p>` : ""}
    </div>`).join("");

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/>
<title>${profile?.name || "Resume"} — CV</title>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Inter','Helvetica Neue',Arial,sans-serif;font-size:10.5pt;color:#1a1a1a;background:#fff;line-height:1.5;padding:40px 48px;max-width:820px;margin:0 auto}
.cv-header{border-bottom:2px solid #1a1a1a;padding-bottom:14px;margin-bottom:18px}
.cv-name{font-size:22pt;font-weight:700;letter-spacing:-.5px}
.cv-title{font-size:11pt;font-weight:500;color:#555;margin:3px 0 8px}
.cv-contacts{display:flex;flex-wrap:wrap;gap:4px 18px;font-size:9pt;color:#444}
.cv-contacts a{color:#444;text-decoration:none}
.cv-section{margin-bottom:18px}
.cv-section-title{font-size:9pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#333;border-bottom:1px solid #ddd;padding-bottom:4px;margin-bottom:10px}
.cv-summary{font-size:10pt;color:#333;line-height:1.65}
.exp-block{margin-bottom:13px}
.exp-header{display:flex;justify-content:space-between;align-items:flex-start}
.exp-role{font-weight:600;font-size:10.5pt}
.exp-company{font-size:9.5pt;color:#555}
.exp-period{font-size:9pt;color:#777;white-space:nowrap;margin-left:10px}
.edu-grade{font-size:9pt;color:#444;font-style:italic}
.bullets{margin-top:5px;padding-left:16px}
.bullets li{font-size:9.5pt;color:#333;margin-bottom:3px}
.sk-table{width:100%;border-collapse:collapse;font-size:9.5pt}
.sk-table td{padding:3px 6px;vertical-align:top}
.sk-cat{font-weight:600;color:#333;white-space:nowrap;width:140px}
.cert-list{padding-left:16px}
.cert-list li{font-size:9.5pt;color:#333;margin-bottom:3px}
.vol-desc{font-size:9pt;color:#555;margin-top:3px}
@media print{body{padding:0}@page{margin:18mm 16mm}}
</style></head><body>
<div class="cv-header">
  <p class="cv-name">${profile?.name || "Your Name"}</p>
  <p class="cv-title">${profile?.title || "Software Engineer"}</p>
  <div class="cv-contacts">
    ${profile?.location ? `<span>📍 ${profile.location}</span>` : ""}
    ${profile?.phone    ? `<span>📞 ${profile.phone}</span>` : ""}
    ${profile?.email    ? `<a href="mailto:${profile.email}">✉ ${profile.email}</a>` : ""}
    ${profile?.linkedin ? `<a href="${profile.linkedin}">${profile.linkedin.replace("https://","")}</a>` : ""}
    ${profile?.website  ? `<a href="${profile.website}">${profile.website.replace("https://","")}</a>` : ""}
  </div>
</div>
${show("summary") && summary ? sec("Professional Summary", `<p class="cv-summary">${summary}</p>`) : ""}
${show("experience") && (experience||[]).length ? sec("Professional Experience", expRows) : ""}
${show("education")  && (education||[]).length  ? sec("Education", eduRows) : ""}
${show("projects")   && (projects||[]).length   ? sec("Selected Projects", projRows) : ""}
${show("technicalSkills") && skillRows ? sec("Technical Skills", `<table class="sk-table">${skillRows}</table>`) : ""}
${show("certifications") && (certifications||[]).length ? sec("Certifications", `<ul class="cert-list">${certList}</ul>`) : ""}
${show("languages") && (languages||[]).length ? sec("Languages", `<table class="sk-table">${langList}</table>`) : ""}
${show("volunteerAndLeadership") && (volunteerAndLeadership||[]).length ? sec("Leadership &amp; Volunteering", volRows) : ""}
</body></html>`;
};

const openPrintWindow = (html) => {
  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 600);
};

// ── Inline resume view ───────────────────────────────────────────────────────
const ResumeView = ({ data, profile, visibleSections }) => {
  const { summary, experience, education, projects, technicalSkills,
          certifications, languages, volunteerAndLeadership } = data;

  // ── CRITICAL: plain includes(), no truthy bypass ─────────────────────────
  const show = (id) => visibleSections.includes(id);

  return (
    <div className="rv">
      <div className="rv__header">
        <div>
          <h1 className="rv__name">{profile?.name || "Your Name"}</h1>
          <p className="rv__title">{profile?.title || "Software Engineer"}</p>
        </div>
        <div className="rv__contacts">
          {profile?.location && <span className="rv__contact-item">📍 {profile.location}</span>}
          {profile?.email    && <a href={`mailto:${profile.email}`} className="rv__contact-item">✉ {profile.email}</a>}
          {profile?.phone    && <a href={`tel:${profile.phone}`} className="rv__contact-item">📞 {profile.phone}</a>}
          {profile?.website  && <a href={profile.website} target="_blank" rel="noreferrer" className="rv__contact-item">{profile.website.replace("https://","")}</a>}
        </div>
      </div>

      {show("summary") && summary && (
        <div className="rv__section">
          <h2 className="rv__section-title">Professional Summary</h2>
          <p className="rv__summary">{summary}</p>
        </div>
      )}
      {show("experience") && (experience||[]).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Professional Experience</h2>
          {sortChronological(experience).map((e, i) => (
            <div key={i} className="rv__block">
              <div className="rv__block-header">
                <div>
                  <p className="rv__role">{e.role}</p>
                  <p className="rv__company">{e.company}{e.location ? ` · ${e.location}` : ""}</p>
                </div>
                <span className="rv__period">{e.startDate} – {e.current ? "Present" : e.endDate}</span>
              </div>
              {(e.bullets||[]).length > 0 && (
                <ul className="rv__bullets">{e.bullets.map((b, bi) => <li key={bi}>{b}</li>)}</ul>
              )}
            </div>
          ))}
        </div>
      )}
      {show("education") && (education||[]).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Education</h2>
          {sortChronological(education).map((e, i) => (
            <div key={i} className="rv__block">
              <div className="rv__block-header">
                <div>
                  <p className="rv__role">{e.degree}</p>
                  <p className="rv__company">{e.institution}{e.location ? `, ${e.location}` : ""}</p>
                  {e.grade && <p className="rv__grade">{e.grade}{e.notes ? ` — ${e.notes}` : ""}</p>}
                </div>
                <span className="rv__period">{e.startDate} – {e.current ? "Present" : e.endDate}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {show("projects") && (projects||[]).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Selected Projects</h2>
          {projects.map((p, i) => (
            <div key={i} className="rv__block">
              <div className="rv__block-header">
                <div>
                  <p className="rv__role">{p.title}</p>
                  {p.stack && <p className="rv__company">{p.stack}</p>}
                </div>
                {p.year && <span className="rv__period">{p.year}</span>}
              </div>
              {(p.bullets||[]).length > 0 && (
                <ul className="rv__bullets">{p.bullets.map((b, bi) => <li key={bi}>{b}</li>)}</ul>
              )}
            </div>
          ))}
        </div>
      )}
      {show("technicalSkills") && technicalSkills && Object.keys(technicalSkills).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Technical Skills</h2>
          <div className="rv__skills">
            {Object.entries(technicalSkills).map(([cat, items]) => (
              <div key={cat} className="rv__skill-row">
                <span className="rv__skill-cat">{cat}</span>
                <span className="rv__skill-items">{items.join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {show("certifications") && (certifications||[]).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Certifications</h2>
          <ul className="rv__cert-list">{certifications.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {show("languages") && (languages||[]).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Languages</h2>
          <div className="rv__skills">
            {languages.map((l, i) => (
              <div key={i} className="rv__skill-row">
                <span className="rv__skill-cat">{l.language}</span>
                <span className="rv__skill-items">{l.level}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {show("volunteerAndLeadership") && (volunteerAndLeadership||[]).length > 0 && (
        <div className="rv__section">
          <h2 className="rv__section-title">Leadership &amp; Volunteering</h2>
          {volunteerAndLeadership.map((v, i) => (
            <div key={i} className="rv__block">
              <div className="rv__block-header">
                <div>
                  <p className="rv__role">{v.role}</p>
                  <p className="rv__company">{v.organisation}</p>
                </div>
                <span className="rv__period">{v.period}</span>
              </div>
              {v.description && <p className="rv__vol-desc">{v.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Page component ───────────────────────────────────────────────────────────
const ResumePage = () => {
  const { profile } = useSite();
  const { data: rawResumeData, loading } = useData(getResumeData);

  const resumeData = Array.isArray(rawResumeData) && rawResumeData.length === 0
    ? null
    : rawResumeData;

  // Resolve visibleSections with strict three-way logic:
  // 1. Not loaded yet → show all (placeholder until data arrives)
  // 2. visibleSections is a saved array → use it exactly as stored
  //    An empty array [] means admin hid everything — that's intentional
  // 3. visibleSections field absent (new install) → show all as safe default
  const visibleSections = React.useMemo(() => {
    if (!resumeData) return ALL_SECTION_IDS;
    if (Array.isArray(resumeData.visibleSections)) return resumeData.visibleSections;
    return ALL_SECTION_IDS;
  }, [resumeData]);

  const lastUpdated = React.useMemo(() => {
    const ts = resumeData?.updatedAt;
    if (!ts) return null;
    try {
      const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
      return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch { return null; }
  }, [resumeData]);

  return (
    <div>
      <Navbar />
      <div className="resume-page">
        <div className="container">
          <div className="resume-page__header">
            <div>
              <h1 className="resume-page__title">Curriculum Vitae</h1>
              <p className="resume-page__sub">
                {lastUpdated
                  ? <>Last updated <strong>{lastUpdated}</strong></>
                  : "Always up to date — edited via the admin dashboard."}
              </p>
            </div>
            <button
              className="btn btn-primary"
              disabled={loading || !resumeData}
              onClick={() => { if (resumeData) openPrintWindow(buildPrintHTML(resumeData, profile, visibleSections)); }}
            >
              {loading
                ? <><FaSpinner className="spin" size={13} /> Loading…</>
                : <><FaDownload size={13} /> Download PDF</>}
            </button>
          </div>

          {loading ? (
            <div className="resume-page__loading">
              <FaSpinner className="spin" size={22} /><p>Loading resume…</p>
            </div>
          ) : resumeData ? (
            <div className="resume-page__card card">
              {visibleSections.length === 0 ? (
                <p style={{ padding: "2rem", color: "var(--text-3)", textAlign: "center" }}>
                  Resume is currently being updated. Please check back soon.
                </p>
              ) : (
                <ResumeView data={resumeData} profile={profile} visibleSections={visibleSections} />
              )}
            </div>
          ) : (
            <div className="resume-page__empty">
              Resume data not found. Please seed your data or add it via the admin dashboard.
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResumePage;